import cx from "classnames";
import { getHoverTipContent } from "@/Utils/Misc";
import { Switch, Text, Flex, ToolIcon, Icon, Button } from "@theorgchartdev/storybook";
import ToolbarPopover from "@/appComponents/Navigation/ContextualToolbar/ToolbarPopover/ToolbarPopover";
import { GetPopoverOverlayProps } from "@/Utils/ComponentLib";
import { GetWSMgr } from "@/Utils/StateLib";
import { useAutoSaveHandler } from "@/hooks/useAutoSaveHandler";
import { appstr } from "@coreapp/utils/appstr";
import { appstrp } from "@coreapp/utils/appstrp";
import { Globals } from "@coreapp/CoreApp/Globals";
import { enumUIModes } from "@coreapp/constants";

const AutoSavePopover = ({ doc, iWiz }) => {
  const wsMgr = GetWSMgr();
  const docMgr = doc.GetDocMgr();

  const {
    canAutoSave,
    autoSaveEnabled,
    disableToggle,
    toggleAutoSaveChart,
    handleSaveClick,
    openCtrlRef
  } = useAutoSaveHandler({ doc, iWiz });

  let baseClass = "refresh-toggle-option";
  let checkedClass = autoSaveEnabled && `${baseClass}-label--checked`;

  const overlayProps = {
    title: appstr("Document status"),
    otherProps: { padding: "8px 12px" },
    sizeProps: {
      maxWidth: 350,
      minWidth: 200
    }
  };

  const reference = docMgr.fSaveQueued ? (
    <Flex gap={"4px"} alignItems={"center"}>
      <Icon name={"cloud"} size={"small"} modifier={"primary"} />
      <Text size={"small"} colorModifier={"theme"} modifier={"medium"}>
        {appstrp("Saving", "...")}
      </Text>
    </Flex>
  ) : (
    <ToolIcon
      dataTestId={"header-auto-save-trigger"}
      name={autoSaveEnabled ? "cloud-check" : "cloud-xmark"}
      size={"medium-small"}
      shape={"round"}
      iconColor={autoSaveEnabled ? "success" : "theme"}
    />
  );

  return (
    <ToolbarPopover
      otherPopoverProps={{
        offsetOptions: -2,
        matchReferenceWidth: false,
        openCtrlRef: openCtrlRef,
        reference: reference
      }}
      overlayProps={GetPopoverOverlayProps(overlayProps)}
    >
      <Flex direction={"column"} gap={"12px"}>
        <Flex alignItems={"center"} gap={"4px"}>
          <Icon
            name={autoSaveEnabled ? "cloud-check" : "cloud-xmark"}
            modifier={autoSaveEnabled ? "success" : "warning"}
            size={"small"}
          />
          <Text
            size={"small"}
            modifier={"medium"}
            colorModifier={autoSaveEnabled ? "success" : "warning"}
          >
            {autoSaveEnabled
              ? appstr("Changes saved automatically")
              : appstr("Changes not saved automatically")}
          </Text>
        </Flex>
        {canAutoSave && (
          <span className={baseClass}>
            <span className={`${baseClass}-switch`}>
              <Switch
                id={"toggleAutoSave"}
                data-testid={"planning-mode-toolbar-autosave-toggle"}
                tooltipProps={getHoverTipContent("Auto-save")}
                onChange={() => toggleAutoSaveChart()}
                checked={autoSaveEnabled}
                disabled={disableToggle}
              />
            </span>
            <span className={cx(`${baseClass}-label`, checkedClass)}>
              <Text size={"small"} colorModifier={disableToggle ? "faintgrey" : null}>
                {appstr("Automatically save changes")}
              </Text>
            </span>
          </span>
        )}
      </Flex>
      {Globals._uiConfig.GetUIMode() === enumUIModes.edit && (
        <Flex
          style={{ marginBottom: 0, marginTop: "auto" }}
          direction={"column"}
          gap={"4px"}
          padding={"16px 0 0 0"}
          className={"oc-left-justify"}
        >
          <Button
            dataTestId={`header-auto-save-btn`}
            onClick={handleSaveClick}
            loading={docMgr.fSaveQueued}
            modifier={"secondary"}
            className={"oc-special-secondary"}
            disabled={!wsMgr.ReadyToSave()}
            size={"small"}
          >
            {appstr("Save now")}
          </Button>
        </Flex>
      )}
    </ToolbarPopover>
  );
};

const AutoSavePopoverWrapper = ({ doc }) => {
  const iWiz = doc?.GetIWizData();
  const currentUIMode = Globals._uiConfig.GetUIMode();
  if (!iWiz || ![enumUIModes.planning, enumUIModes.edit, enumUIModes.chart].includes(currentUIMode))
    return;

  if (currentUIMode === enumUIModes.chart && doc.IsServerChart()) return;

  return <AutoSavePopover doc={doc} iWiz={iWiz} />;
};

export default AutoSavePopoverWrapper;
